import React from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { Container } from '../Container';
import { ButtonLink } from '../ButtonLink';
import { GridLines } from '../GridLines';
import { Reveal } from '../Reveal';
import { AppImage } from '../AppImage';
import { ImageBackdrop } from '../ImageBackdrop';
import { heroImage } from '../../data/homeImages';
type HeroVariant = 'overlap' | 'stacked' | 'editorial';
interface HeroProps {
  variant?: HeroVariant;
}
const heroWords = 'We design websites that make small companies look inevitable.'.split(' ');
const visualLayouts: Record<HeroVariant, string> = {
  overlap: 'lg:mt-9 lg:-ml-10',
  stacked: 'lg:mt-0 lg:-ml-5',
  editorial: 'lg:mt-20 lg:-ml-14'
};
export function Hero({
  variant = 'overlap'
}: HeroProps) {
  const prefersReducedMotion = useReducedMotion();
  const {
    scrollY
  } = useScroll();
  const parallaxY = useTransform(scrollY, value => value * -0.15);
  const layout = visualLayouts[variant];
  return <section className="relative isolate min-h-[92vh] overflow-hidden border-b border-hairline bg-canvas pb-20 pt-[96px] lg:flex lg:items-center lg:pb-24 lg:pt-[136px]" data-id="element-486" data-mp="1mvt4bol76d0h">
      <ImageBackdrop src={heroImage.image} opacityClass="opacity-[0.06]" blurClass="blur-[40px]" overlayClass="bg-white/40" data-id="element-487" data-mp="1rt2tr48l6oob" />
      <GridLines data-id="element-488" data-mp="1hxrkv2jv0boh" />
      <Container className="relative z-10" data-id="element-489" data-mp="1hzc6g2o74ka7">
        <div className="grid items-center gap-14 lg:grid-cols-[58fr_42fr] lg:gap-0" data-id="element-490" data-mp="25mk9sf22dj9l">
          <div className="relative z-20 lg:pr-10" data-id="element-491" data-mp="2alng02ts5vj5">
            <h1 aria-label="We design websites that make small companies look inevitable." className="relative max-w-[16ch] font-serif text-[clamp(48px,6vw,88px)] font-normal leading-[1.05] tracking-[-0.03em] text-ink lg:mr-[-40px]" data-id="element-492" data-mp="2al5b6ht1y8zm">
              {heroWords.map((word, index) => <span key={`${word}-${index}`} aria-hidden="true" className="mr-[0.22em] inline-block overflow-hidden align-top last:mr-0" data-id="element-493" data-mp="2ojv73u44y2me" data-mpi={index}>
                  <motion.span className="inline-block" initial={prefersReducedMotion ? false : {
                y: '108%'
              }} animate={{
                y: 0
              }} transition={{
                duration: 0.5,
                delay: index * 0.08,
                ease: [0.23, 1, 0.32, 1]
              }} data-id="element-494" data-mp="2y9jzdhpsx703">
                    {word}
                  </motion.span>
                </span>)}
            </h1>

            <Reveal delay={0.68} data-id="element-495" data-mp="2aj7uygtrlse9">
              <p className="mt-8 max-w-[54ch] text-body text-muted" data-id="element-496" data-mp="2akiz4hr4h25w">
                A small independent studio for founder-led companies who have outgrown the site they
                built themselves.
              </p>
            </Reveal>

            <Reveal delay={0.74} data-id="element-497" data-mp="1wjyerh5k6yaa">
              <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-498" data-mp="1wjonzrcown3j">
                <ButtonLink to="/work" data-id="element-499" data-mp="c2fi89vonvmq">See our work</ButtonLink>
                <ButtonLink to="/contact" variant="secondary" data-id="element-500" data-mp="c40f465gzqnr">
                  Book a call
                </ButtonLink>
              </div>
            </Reveal>

            <Reveal delay={0.8} data-id="element-501" data-mp="m1esty00e1cd">
              <p className="mt-14 flex items-center gap-2.5 border-t border-hairline pt-6 text-small text-muted" data-id="element-502" data-mp="lxz8ogrha243">
                <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-accent" data-id="element-503" data-mp="3pgsm8tk9aqy9" />
                Currently taking on new projects.
              </p>
            </Reveal>
          </div>

          <motion.div style={prefersReducedMotion ? undefined : {
          y: parallaxY
        }} className="relative z-10 mx-auto w-full max-w-[560px] lg:mx-0 lg:max-w-none" data-id="element-504" data-mp="3udiy14p130dv">
            <AppImage src={heroImage.image} alt={heroImage.alt} ratio="16:10" className={`relative z-10 ${layout}`} data-id="element-505" data-mp="74nrhj6ryfk4" />
          </motion.div>
        </div>
      </Container>
    </section>;
}